import React from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { TbBrandBooking, TbUsers, TbMap2, TbNews } from 'react-icons/tb';
import { HiOutlineArrowTrendingUp, HiOutlineCalendarDays } from 'react-icons/hi2';
import { MdOutlineTour } from 'react-icons/md';
import { BsFlag } from 'react-icons/bs';
import { AiOutlineTags } from 'react-icons/ai';
import { Card } from '../../ui/Card';
import Charts, { getCountriesData } from '../sections/Charts';
import { useCountries, useTours, useBookings, usePosts, useAgents } from '../../../hooks';

const Overview = () => {
  const { tours, loading: toursLoading } = useTours();
  const { bookings, loading: bookingsLoading } = useBookings();
  const { countries, loading: countriesLoading } = useCountries();
  const { posts, loading: postsLoading } = usePosts('posts');
  const { agents, loading: agentsLoading } = useAgents();
  const navigate = useNavigate();

  const stats = [
    {
      title: 'Total Bookings',
      value: bookings.length,
      loading: bookingsLoading,
      icon: TbBrandBooking,
      color: 'primary',
      path: '/dashboard/bookings',
      trend: '+12%', // This could be calculated from real data if available
    },
    {
      title: 'Tour Packages',
      value: tours.length,
      loading: toursLoading,
      icon: TbMap2,
      color: 'secondary',
      path: '/dashboard/tours',
    },
    {
      title: 'Safari Updates',
      value: posts.length,
      loading: postsLoading,
      icon: TbNews,
      color: 'tertiary',
      path: '/dashboard/safari-updates',
    },
    {
      title: 'Registered Agents',
      value: agents.length,
      loading: agentsLoading,
      icon: TbUsers,
      color: 'info',
      path: '/dashboard/agents',
    },
  ];

  const bookedCountriesCount = getCountriesData(bookings)?.length || 0;

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Welcome Header */}
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
            Dashboard Overview
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1'>
            Welcome back! Here&apos;s what's happening with Dav Safaris today.
          </p>
        </div>
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-2 px-4 py-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm'>
            <HiOutlineCalendarDays className='w-5 h-5 text-primary-600' />
            <div className='flex flex-col'>
              <span className='text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-none'>Today</span>
              <span className='text-sm font-bold text-gray-900 dark:text-white leading-tight'>
                {moment().format('MMMM Do, YYYY')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        {stats.map((stat, index) => (
          <Card
            key={stat.title}
            variant='glass'
            hoverable
            onClick={() => navigate(stat.path)}
            className='group overflow-hidden'
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className='p-6'>
              <div className='flex items-start justify-between'>
                <div className={`
                  p-3 rounded-2xl bg-${stat.color}-500/10 text-${stat.color}-600 
                  dark:bg-${stat.color}-500/20 dark:text-${stat.color}-400
                  group-hover:scale-110 transition-transform duration-300
                `}>
                  <stat.icon className='w-6 h-6' />
                </div>
                {stat.trend && (
                  <div className='flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-bold bg-emerald-500/10 px-2 py-1 rounded-full'>
                    <HiOutlineArrowTrendingUp className='w-3 h-3' />
                    {stat.trend}
                  </div>
                )}
              </div>
              <div className='mt-4'>
                <h4 className='text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                  {stat.title}
                </h4>
                <div className='flex items-baseline gap-2 mt-1'>
                  <span className='text-3xl font-bold text-gray-900 dark:text-white'>
                    {stat.loading ? (
                      <div className='h-8 w-16 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-lg' />
                    ) : (
                      stat.value
                    )}
                  </span>
                </div>
              </div>
            </div>
            {/* Subtle background decoration */}
            <div className={`absolute -right-4 -bottom-4 w-24 h-24 bg-${stat.color}-500/5 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500`} />
          </Card>
        ))}
      </div>

      {/* Special Stats: Countries */}
      <Card variant='glass' className='overflow-hidden'>
        <div className='flex flex-col md:flex-row items-center divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-800'>
          <div className='flex-1 p-8 text-center md:text-left'>
            <div className='text-sm font-medium text-gray-500 dark:text-gray-400 mb-2'>
              Active Countries
            </div>
            <div className='flex items-center justify-center md:justify-start gap-4'>
              <div className='text-4xl font-bold text-gray-900 dark:text-white font-heading'>
                {countriesLoading ? '...' : countries.length}
              </div>
              <div className='h-8 w-px bg-gray-200 dark:bg-gray-700 mx-2' />
              <div className='text-left'>
                <div className='text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-tighter'>
                  Market Reach
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  Global presence across regions
                </div>
              </div>
            </div>
          </div>
          <div className='flex-1 p-8 text-center md:text-left'>
            <div className='text-sm font-medium text-gray-500 dark:text-gray-400 mb-2'>
              Booked Countries
            </div>
            <div className='flex items-center justify-center md:justify-start gap-4'>
              <div className='text-4xl font-bold text-primary-600 dark:text-primary-400 font-heading'>
                {bookingsLoading ? '...' : bookedCountriesCount}
              </div>
              <div className='h-8 w-px bg-gray-200 dark:bg-gray-700 mx-2' />
              <div className='text-left'>
                <div className='text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-tighter'>
                  Conversion Rate
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  Countries with active sales
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
        {[
          {
            title: 'Create Tour',
            desc: 'Add a new safari package',
            icon: MdOutlineTour,
            path: '/dashboard/tours/new',
            color: 'primary'
          },
          {
            title: 'Add Country',
            desc: 'New travel destination',
            icon: BsFlag,
            path: '/dashboard/countries/new',
            color: 'primary'
          },
          {
            title: 'New Category',
            desc: 'Tour type or theme',
            icon: AiOutlineTags,
            path: '/dashboard/categories/new',
            color: 'secondary'
          },
          {
            title: 'View Bookings',
            desc: 'Traveler reservations',
            icon: TbBrandBooking,
            path: '/dashboard/bookings',
            color: 'emerald'
          },
        ].map((action, index) => (
          <button
            key={action.title}
            type='button'
            onClick={() => navigate(action.path)}
            style={{ animationDelay: `${index * 100}ms` }}
            className='
              group relative flex items-center gap-3 p-4 
              bg-white/50 dark:bg-gray-800/50 backdrop-blur-md 
              rounded-2xl border border-gray-200 dark:border-gray-700 
              hover:border-primary-500/50 hover:shadow-xl hover:shadow-primary-500/10 
              transition-all duration-300 text-left
            '
          >
            <div className={`
              p-2.5 rounded-xl bg-${action.color}-500/10 text-${action.color}-600 
              dark:bg-${action.color}-500/20 dark:text-${action.color}-400
              group-hover:scale-110 transition-transform
            `}>
              <action.icon className='w-5 h-5' />
            </div>
            <div>
              <h4 className='text-sm font-bold text-gray-900 dark:text-white leading-none mb-1'>
                {action.title}
              </h4>
              <p className='text-[11px] text-gray-400 dark:text-gray-500 font-medium'>
                {action.desc}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Charts Section */}
      <div className='animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300'>
        <Charts data={bookings} />
      </div>
    </div>
  );
};

export default Overview;
